import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { UtilisationTier } from '@/types';

interface UtilisationSliderProps {
  tiers: UtilisationTier[];
  onBoundaryChange: (leftTierId: string | number, rightTierId: string | number, boundaryValue: number) => void;
}

// How far the slider scale extends beyond 100% when an over-utilisation band exists
const OVER_UTILISATION_MAX = 150; // visual cap at 150% for display purposes

export function UtilisationSlider({ tiers, onBoundaryChange }: UtilisationSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  // Tracks the rendered pixel width of the slider track so that band segments,
  // drag handles, and the scale ruler all re-compute correctly after first mount
  // (the tab is conditionally rendered so offsetWidth is 0 before the element exists).
  const [sliderWidth, setSliderWidth] = useState<number>(0);

  useLayoutEffect(() => {
    if (!sliderRef.current) return;
    // Measure immediately after the DOM is painted
    setSliderWidth(sliderRef.current.offsetWidth);

    // Also keep the ruler accurate if the panel is resized
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setSliderWidth(entry.contentRect.width);
    });
    ro.observe(sliderRef.current);
    return () => ro.disconnect();
  }, []);

  const enabledTiers = tiers
    .filter(tier => tier.enabled)
    .sort((a, b) => a.minPercentage - b.minPercentage);

  // Has at least one band that explicitly covers >100%
  const hasOverBand = enabledTiers.some(t => t.maxPercentage > 100);
  // The visual scale of the slider (how many "%" units span the full width)
  const scaleMax = hasOverBand ? OVER_UTILISATION_MAX : 100;

  // Always use dark text — the utilisation tier palette uses light pastels throughout,
  // so dark text gives consistent WCAG-compliant contrast without mixing black/white labels.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getTextColor = (_bgColor: string): string => 'rgba(0, 0, 0, 0.75)';

  const percentToPixels = (percent: number): number => {
    if (!sliderWidth) return 0;
    // Cap visual position at scaleMax
    return (Math.min(percent, scaleMax) / scaleMax) * sliderWidth;
  };

  const pixelsToPercent = (pixels: number): number => {
    if (!sliderWidth) return 0;
    return Math.round((pixels / sliderWidth) * scaleMax);
  };

  const handleMouseDown = (index: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    setDraggingIndex(index);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (draggingIndex === null || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const newPercent = pixelsToPercent(x);

      const leftTier = enabledTiers[draggingIndex];
      const rightTier = enabledTiers[draggingIndex + 1];

      if (!leftTier || !rightTier) return;

      // Handles before an over-utilisation band can be dragged anywhere up to scaleMax - 1
      // Handles between non-over bands are capped at rightTier.maxPercentage - 1
      const isRightBandOverUtilisation = rightTier.maxPercentage > 100;
      const maxAllowed = isRightBandOverUtilisation ? scaleMax - 1 : rightTier.maxPercentage - 1;
      const minAllowed = leftTier.minPercentage + 1;

      const clampedPercent = Math.max(minAllowed, Math.min(maxAllowed, newPercent));
      onBoundaryChange(leftTier.id, rightTier.id, clampedPercent);
    };

    const handleMouseUp = () => setDraggingIndex(null);

    if (draggingIndex !== null) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingIndex, enabledTiers, onBoundaryChange, scaleMax]);

  const getInstructionText = () => {
    if (enabledTiers.length === 0) return 'Enable bands below to see them displayed on the slider.';
    if (enabledTiers.length === 1) return 'Enable additional bands to adjust thresholds with draggable handles.';
    return hasOverBand
      ? 'Drag the circular handles to adjust band thresholds. The slider extends past 100% to show over-utilisation. Ranges update in real-time.'
      : 'Drag the circular handles to adjust band thresholds. Ranges update in real-time.';
  };

  // Build scale markers: always show 0, 25, 50, 75, 100 — plus 125, 150 when scale > 100
  const scaleMarkers = [0, 25, 50, 75, 100, ...(hasOverBand ? [125, 150] : [])];

  return (
    <div className="space-y-4">
      <div className="relative">
        {/* Slider track */}
        <div
          ref={sliderRef}
          className="relative h-20 bg-gray-100 rounded-lg overflow-hidden"
          style={{ cursor: draggingIndex !== null ? 'ew-resize' : 'default' }}
        >
          {/* Coloured band segments */}
          {enabledTiers.map((tier, index) => {
            const startPos = percentToPixels(tier.minPercentage);
            const nextTier = enabledTiers[index + 1];
            // Visual end: next band's start, or end of scale for last band
            const visualEndPercent = nextTier ? nextTier.minPercentage : scaleMax;
            const endPos = percentToPixels(visualEndPercent);
            const width = endPos - startPos;
            if (width <= 0) return null;

            const textColor = getTextColor(tier.color);
            const rangeText =
              tier.minPercentage === tier.maxPercentage
                ? `${tier.minPercentage}%`
                : tier.maxPercentage >= 999
                  ? `${tier.minPercentage}%+`
                  : `${tier.minPercentage}% – ${tier.maxPercentage}%`;

            return (
              <div
                key={tier.id}
                className="absolute top-0 bottom-0 flex items-center justify-center transition-all duration-150"
                style={{ left: `${startPos}px`, width: `${width}px`, backgroundColor: tier.color }}
              >
                <span className="text-sm font-medium select-none pointer-events-none" style={{ color: textColor }}>
                  {rangeText}
                </span>
              </div>
            );
          })}

          {enabledTiers.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
              No bands enabled
            </div>
          )}



          {/* Drag handles between adjacent bands */}
          {enabledTiers.length >= 2 &&
            enabledTiers.slice(0, -1).map((tier, index) => {
              const nextTier = enabledTiers[index + 1];
              // Handles before the over-utilisation band sit at the 100% boundary
              const handlePercent = nextTier.minPercentage;
              const position = percentToPixels(handlePercent);
              const isHovered = hoveredIndex === index;
              const isDragging = draggingIndex === index;

              return (
                <div
                  key={`handle-${tier.id}`}
                  className="absolute top-0 bottom-0 flex items-center justify-center"
                  style={{
                    left: `${position}px`,
                    transform: 'translateX(-50%)',
                    width: '24px',
                    cursor: 'ew-resize',
                    zIndex: isDragging ? 20 : isHovered ? 15 : 10,
                  }}
                  onMouseDown={handleMouseDown(index)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div
                    className="rounded-full bg-white shadow-lg border-2 transition-all duration-150"
                    style={{
                      width: isHovered || isDragging ? '20px' : '16px',
                      height: isHovered || isDragging ? '20px' : '16px',
                      borderColor: isHovered || isDragging ? '#3b82f6' : '#d1d5db',
                    }}
                  />
                </div>
              );
            })}
        </div>

        {/* Scale ruler */}
        <div className="relative h-6 mt-1">
          {scaleMarkers.map((pct) => {
            if (!sliderWidth) return null;
            const pos = (pct / scaleMax) * sliderWidth;
            const isHundred = pct === 100 && hasOverBand;
            return (
              <div
                key={pct}
                className={`absolute text-xs ${isHundred ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
                style={{
                  left: `${pos}px`,
                  transform: pct === 0 ? 'none' : pct === scaleMax ? 'translateX(-100%)' : 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                {pct}%{pct === scaleMax && hasOverBand ? '+' : ''}
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-sm text-muted-foreground">{getInstructionText()}</p>
    </div>
  );
}
